import React from 'react';
import { getMemberInfo } from '@/hooks/useMembers';
import MAvatar from '@/components/ui/atoms/MAvatar';
import ActionCard from './components/ActionCard';
import DashboardQuickActions from './components/DashboardQuickActions';
import FamilyWidget from './components/FamilyWidget';
import DocumentSend from './components/DocumentSend';
import Insights from './components/Insights';
import UpdatesWidget from './components/UpdatesWidget';
import styles from './dashboard.module.css';

import { useNavigate } from 'react-router-dom';
import { useYearWindow } from '@/hooks/useYearWindow';
import { useAvatar } from '@/hooks/useAvatar';
import { useFamilyStats } from '@/hooks/useFamilyStats';
import { useDynamicDashboardMessage } from '@/utils/dashboardMessages';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MFab from '@/components/ui/atoms/MFab';
import { MTourTrigger } from '@/components/ui/organisms/MTourTrigger';
import { MHelpIcon } from '@/components/ui/atoms/MHelpIcon';
import { ProfileStrength } from '@/types/familyStats';
import { useActionNotifications } from '@/hooks/useActionNotifications';
import { Nudge} from './components/Nudge';
import { useInfoCards } from '@/hooks/useInfoCards';

const YEARS_WINDOW = 5;

import { useNotesContext } from '@/context/NotesContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearSelectedMember } from '@/store/authSlice';
import { useSubscription } from '@/hooks/useSubscription';
import SubscriptionExpired from './components/SubscriptionExpired';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const Dashboard = () => {
  const tourTriggerRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const { subscription, fetchCurrentSubscription } = useSubscription();
  
  // Clear selectedMember IMMEDIATELY before any other operations
  React.useEffect(() => {
    dispatch(clearSelectedMember());
  }, [dispatch]);
  
  // Always use logged-in member for dashboard (ignore selectedMember)
  const memberInfo = getMemberInfo(true);
  const memberId = memberInfo.id;
  const { actions } = useActionNotifications();
  const { notifications } = useInfoCards(memberId);
  // Check for uncategorized documents to determine if spacer should show
  const [uncategorizedCount, setUncategorizedCount] = React.useState(0);
  
  React.useEffect(() => {
    const checkUncategorizedDocuments = async () => {
      try {
        const { documentService } = await import('@/services/documents.service');
        const docService = documentService(memberId);
        const response = await docService.getDocumentTypeCounts({ mode: 'dashboard' }) as { uncategorized?: number };
        setUncategorizedCount(response?.uncategorized || 0);
      } catch (error) {
        console.error("Error checking uncategorized documents:", error);
        setUncategorizedCount(0);
      }
    };
    
    if (memberId) {
      checkUncategorizedDocuments();
    }
  }, [memberId]);

  // Fetch subscription status for all logged-in members
  React.useEffect(() => {
    if (memberId) {
      fetchCurrentSubscription();
    }
  }, [memberId, fetchCurrentSubscription]);
  
  // Track GTM user_data_loaded event for home screen
  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.HOMEPAGE,
      screenName: ANALYTICS_SCREEN_NAMES.HOME,
    });
  }, [memberInfo?.id]);
  
  const { avatarUrl } = useAvatar();
  const navigate = useNavigate();


  // Family stats for dynamic messaging
  const { memberStats, loading: memberStatsLoading, fetchMemberStats } = useFamilyStats({ memberInfo });
  
  // Get profile strength with fallback to LOW
  const profileStrength = memberStats?.profileStrength || ProfileStrength.LOW;
  
  // Get dynamic dashboard message
  const { message: dynamicMessage, messageKey } = useDynamicDashboardMessage(
    profileStrength, 
    memberInfo.firstName || ''
  );

  // Fetch unread notes count from context on dashboard mount
  const { fetchUnreadCount } = useNotesContext();
  React.useEffect(() => {
    localStorage.removeItem("documentFilters");
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Refetch dashboard data when selected member changes
  React.useEffect(() => {
    if (memberId) {
      fetchMemberStats(memberId);
    }
  }, [memberId, fetchMemberStats]);

  const {
    selectedYear,
  } = useYearWindow([], 0, undefined, YEARS_WINDOW, { fetchSummary: () => {} }, memberInfo.dobYear || 1940);

  // Persist selectedYear to sessionStorage to sync with DashboardDetail
  React.useEffect(() => {
    if (selectedYear) {
      sessionStorage.setItem('dashboardDetailSelectedYear', String(selectedYear));
    }
  }, [selectedYear]);

  // Check subscription status for all logged-in members
  if (subscription) {
    const subscriptionStatus = subscription.status?.toLowerCase();
    const today = new Date();
    const endDate = new Date(subscription.endDate);
    const isPastEndDate = today > endDate;
    // Show SubscriptionExpired if status is 'expired' OR if status is 'cancelled' AND past end date
    if (subscriptionStatus === 'expired' || (subscriptionStatus === 'cancelled' && isPastEndDate)) {
      return <SubscriptionExpired />;
    }
  }

  return (
    <div className="min-h-screen flex flex-col px-6">
      <div className="dashboard-ellipses">
          <div className="dashboard-ellipse dashboard-ellipse-1"></div>
          <div className="dashboard-ellipse dashboard-ellipse-2"></div>
          <div className="dashboard-ellipse dashboard-ellipse-3"></div>
          <div className="dashboard-ellipse dashboard-ellipse-4"></div>
          <div className="dashboard-ellipse dashboard-ellipse-5"></div>
        </div>
      {/* Top Profile Row */}
      <div className={styles.dashboardProfileRow}>
        <div className={styles.headerHelpIcon} data-wizard-help-icon>
          <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
        </div>
        <button onClick={() => navigate('/profile/')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginLeft: 'auto' }} data-profile-avatar>
          <MAvatar
            size="md"
            text={`${memberInfo.firstName || ''}${memberInfo.lastName ? ' ' + memberInfo.lastName : ''}`.trim()}
            type={memberInfo.avatar ? 'dp' : 'no-dp'}
            src={avatarUrl || undefined}
            alt={`${memberInfo.firstName || ''} ${memberInfo.lastName || ''}`.trim()}
            memberId={memberInfo.id}
          />
        </button>
      </div>
      <div className={styles.dashboardIntro} data-dashboard-welcome>
        Welcome {memberInfo.firstName || ''},<br />
        <span 
          key={messageKey}
          className={`${styles.dashboardIntroMessage} ${messageKey > 0 ? styles.messageTransition : ''}`}
        >
          {dynamicMessage}
        </span>
      </div>
      {/* Dashboard widgets */}
      <MSpacer mt={16} />
      <div className={memberInfo.isExperienced ? styles.dashboardWidgetsContainer :  ""}>
        {memberInfo.isExperienced && !memberStatsLoading && memberStats && (
          <DashboardQuickActions
            onAddHealthMemory={() => navigate('/documents/health-memory', { state: { backUrl: '/dashboard' } })}
            onAddDocument={() => navigate('/documents', { state: { backUrl: '/dashboard' } })}
            onAddFamilyNote={() => navigate('/profile/family/notes', { state: { backUrl: '/dashboard' } })}
            healthSummaryReady={memberStats.healthSummaryReady}
            emergencyCardReady={memberStats.emergencyCardReady}
          />
        )}
        <ActionCard uncategorizedCount={uncategorizedCount} />
        {(actions && actions.length > 0) || uncategorizedCount > 0 ? <MSpacer mt={24} /> : null}
        <Insights />
        {memberInfo.isExperienced && <Nudge />}
        <MSpacer mt={24} />
        {!(memberInfo.family_member_type === 'fm' && notifications.length === 0) && (
          <UpdatesWidget notifications={notifications} />
        )}
        {!(memberInfo.family_member_type === 'fm' && notifications.length === 0) && (
          <MSpacer mt={24} />
        )}
         <FamilyWidget />
        <MSpacer mt={24} />
        <DocumentSend />
        <MSpacer mt={60} />
      </div>
      <MFab />
      <MTourTrigger
        ref={tourTriggerRef}
        page={memberInfo.isExperienced ? 'dashboard-experienced' : 'dashboard-inexperienced'}
      />
    </div>
  );
};

export default Dashboard;